import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, Typography, Button, TextField, FormControl, Chip, Autocomplete, ListItemText, FormControlLabel, Checkbox, InputLabel, Select, MenuItem, Grid, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';

function AssignCustomerAndEmployeeModal(props) {
  const [selectedCustomers,   setSelectedCustomers] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]); // State for selected employees
  const [startDate, setStartDate] = useState();
  const [startTime, setStartTime] = useState();
  const [endTime, setEndTime] = useState();
  const [scheduleName, setScheduleName] = useState('');
  const [notes, setNotes] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({
    selectedCustomers: false,
    selectedEmployees: false,
    startTime: false,
    endTime: false
  });
  const [customersOpen, setCustomersOpen] = useState(false);
  const [applyChangesToFuture, setApplyChangesToFuture] = useState(false);
  const [taskType, setTaskType] = useState(props.schedule?.task_type || '');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [preAuthList, setPreAuthList] = useState([]); 
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [customersToRemove, setCustomersToRemove] = useState([]);
  const [employeesToRemove, setEmployeesToRemove] = useState([]);

  const scheduleId = props.schedule?.schedule_id;
  const scheduleDateRaw = props.schedule?.date;
  const scheduleStartTime = props.schedule?.start_time;
  const scheduleEndTime = props.schedule?.end_time;
  const scheduleNotes = props.schedule?.notes;
  const scheduleTaskType = props.schedule?.task_type;
  const scheduleMaxCapacity = props.schedule?.max_capacity;

  const initialDataRef = useRef(null);
  const initialAssignedCustomerIdsRef = useRef([]);
  const initialAssignedEmployeeIdsRef = useRef([]);
  const isSubmittingRef = useRef(false);

  // const isAdmin = props.isAdmin; // Using passed prop to check if the user is an admin
  const entityLabelPlural = props.isDayCare ? 'Members' : 'Customers';

  const assignAllOption = { cus_id: 'assign_all', cus_name: 'Assign All' };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Prevent duplicate submissions from race conditions
    if (isSubmittingRef.current) {
      console.log('Submission already in progress, skipping');
      return;
    }
    
    if (!validate()) {
      return;
    }
    
    isSubmittingRef.current = true;

  // Compute current assigned customers: initial - removed + added
  const initialCustomerIds = new Set(initialAssignedCustomerIdsRef.current);
  const currentCustomerIds = new Set([
    ...initialAssignedCustomerIdsRef.current.filter(id => !customersToRemove.some(item => item.customer.cus_id === id)),
    ...selectedCustomers.map(c => c.cus_id)
  ]);

  // Calculate additions and removals by comparing initial vs current
  const customersToAdd = selectedCustomers.filter(c => !initialCustomerIds.has(c.cus_id));
  const customersToDelete = customersToRemove.filter(item => initialCustomerIds.has(item.customer.cus_id));

  const remainingCustomers = currentCustomerIds.size;
    const originalMaxCapacityValue = props.schedule?.max_capacity;
    const hasOriginalCapacity = originalMaxCapacityValue !== undefined && originalMaxCapacityValue !== null;
    const normalizedOriginalMaxCapacity = hasOriginalCapacity ? Number(originalMaxCapacityValue) : null;
    const updatedMaxCapacity = maxCapacity === '' || maxCapacity === null
      ? (hasOriginalCapacity ? Number(originalMaxCapacityValue) : null)
      : Number(maxCapacity);

    if(updatedMaxCapacity !== null && remainingCustomers > updatedMaxCapacity) {
      props.onError(`The number of ${entityLabelPlural} assigned to the session exceeds the maximum capacity`);
      isSubmittingRef.current = false;
      return;
    }

    let sday = startDate ? startDate : props.schedule.date;
    let st = startTime ? startTime : props.schedule.start_time;
    let et = endTime ? endTime : props.schedule.end_time;

    let session = {
      schedule_id: props.schedule.schedule_id,
      customers: selectedCustomers.map(customer => customer.cus_id),
      date: sday,
      start_time: moment(st).date(moment(startDate).date()).utc().format('YYYY-MM-DD HH:mm:00'),
      end_time:  moment(et).date(moment(startDate).date()).utc().format('YYYY-MM-DD HH:mm:00'),
      notes: notes,
      reccurence: applyChangesToFuture
    }

    let schedule = { 
      date: sday,
      start_time: moment(st).date(moment(sday).date()).utc().format('YYYY-MM-DD HH:mm:00'),
      end_time: moment(et).date(moment(sday).date()).utc().format('YYYY-MM-DD HH:mm:00'),
      notes: sessionNotes,
      resource_id: props.schedule.resource_id,
      max_capacity: updatedMaxCapacity,
      schedule_name: scheduleName,
      task_type: taskType,
      reccurence: applyChangesToFuture
    };
    if(props.user.permissions.ScheduleSessionForOtherEmployees) {
      // Compute employee additions and removals by comparing initial vs current state
      const initialEmployeeIds = new Set(initialAssignedEmployeeIdsRef.current);
      const employeesToAdd = selectedEmployees.filter(emp => !initialEmployeeIds.has(emp.id));
      const employeesToDelete = employeesToRemove.filter(emp => initialEmployeeIds.has(emp.id));
      
      if(employeesToAdd.length > 0) {
        try{
          await axios.post(APIURL + '/master_schedule/employee', { schedule_id: props.schedule.schedule_id, employee_ids: employeesToAdd.map(employee => employee.id), reccurence: applyChangesToFuture });
          props.onSuccess(`Employees assigned to session successfully`);
        } catch (error) {
          props.onError(`An error occurred while assigning employees to session`);
          isSubmittingRef.current = false;
          return;
        }
      }

      if (employeesToDelete.length > 0) {
        try {
          for (const employee of employeesToDelete) {
            await axios.delete(APIURL + '/master_schedule/employee/' + employee.id, { data: { schedule_id: props.schedule.schedule_id, reccurence: applyChangesToFuture } });
          }
          props.onSuccess('Employees removed from session successfully');
        } catch (error) {
          props.onError('An error occurred while removing employees from session');
          isSubmittingRef.current = false;
          return;
        }
      }
    }
    
    if(customersToAdd.length > 0) {
      try {
        const sessionPayload = {
          ...session,
          customers: customersToAdd.map(customer => customer.cus_id)
        };
        await axios.post(APIURL + '/sessions', sessionPayload);
        props.onSuccess(`${entityLabelPlural} assigned to session successfully`);
      } catch (error) {
        console.log(error);
          if(error.response) {
            props.onError(error.response.data.message);
          } else {
            props.onError(`An error occurred while assigning ${entityLabelPlural} to session`);
          }
          isSubmittingRef.current = false;
          return;
      }
    } 

    if (customersToDelete.length > 0) {
      try {
        for (const { session: sessionToRemove } of customersToDelete) {
          if (sessionToRemove.task_id && sessionToRemove.check_in_time !== null) {
            await axios.put(APIURL + '/sessions/attendance/' + sessionToRemove.session_id, {
              tas_id: sessionToRemove.task_id,
              tas_is_active: 0,
              tas_type: null,
              tas_start_time: null,
              tas_end_time: null,
              check_in_time: null,
              check_out_time: null,
              task_id: sessionToRemove.task_id,
              cus_id: sessionToRemove.customer_id,
              emp_id: props.user.emp_id,
              company_id: props.user.comp_id
            });
          }
          await axios.delete(APIURL + '/sessions/' + sessionToRemove.session_id, { data: { reccurence: applyChangesToFuture } });
        }
        props.onSuccess(`${entityLabelPlural} removed from session successfully`);
      } catch (error) {
        props.onError(`An error occurred while removing ${entityLabelPlural.toLowerCase()} from session`);
        isSubmittingRef.current = false;
        return;
      }
    }

  const originalDate = props.schedule?.date ? props.schedule.date.substring(0, 10) : '';
    const originalStartValue = props.schedule?.start_time;
    const originalEndValue = props.schedule?.end_time;
    const currentStartValue = startTime || originalStartValue;
    const currentEndValue = endTime || originalEndValue;
    const currentStartMoment = currentStartValue ? moment(currentStartValue) : null;
    const originalStartMoment = originalStartValue ? moment(originalStartValue) : null;
    const currentEndMoment = currentEndValue ? moment(currentEndValue) : null;
    const originalEndMoment = originalEndValue ? moment(originalEndValue) : null;
    const startChanged = originalStartMoment ? !currentStartMoment?.isSame(originalStartMoment) : !!currentStartMoment;
    const endChanged = originalEndMoment ? !currentEndMoment?.isSame(originalEndMoment) : !!currentEndMoment;

    if(
      (startDate || '') !== originalDate ||
      startChanged ||
      endChanged ||
      sessionNotes !== (props.schedule?.notes || '') ||
      scheduleName !== (props.schedule?.schedule_name || '') ||
      String(taskType || '') !== String(props.schedule?.task_type || '') ||
      updatedMaxCapacity !== normalizedOriginalMaxCapacity
    ) {
      try {
        await axios.put(APIURL + '/master_schedule/' + props.schedule.schedule_id, schedule);
        props.onSuccess('Schedule updated successfully');
      } catch (error) {
        if(error.response) {
          props.onError(error.response.data.message);
        } else {
          props.onError('An error occurred while updating schedule');
        }
        isSubmittingRef.current = false;
        return;
      }
    }

    isSubmittingRef.current = false;
    props.reloadSessionData();
    props.reloadScheduleData();
    props.onClose();
  };

  const handleDeleteEmployee = (employee) => {
    setEmployeesToRemove(prev => {
      if (prev.some(item => item.id === employee.id)) {
        return prev;
      }
      return [...prev, employee];
    });
  };

  const handleDeleteCustomer = (customer) => {
    const session = props.sessions.find(s => s.schedule_id === props.schedule.schedule_id && s.customer_id === customer.cus_id);
    if (!session) {
      return;
    }
    setCustomersToRemove(prev => {
      if (prev.some(item => item.customer.cus_id === customer.cus_id)) {
        return prev;
      }
      return [...prev, { customer, session }];
    });
  };  

  const validate = () => {
    let errors;
    const originalDate = props.schedule?.date ? props.schedule.date.substring(0, 10) : '';
    const currentDateValue = startDate !== undefined ? startDate : originalDate;
    const originalStartValue = props.schedule?.start_time;
    const originalEndValue = props.schedule?.end_time;
    const currentStartValue = startTime || originalStartValue;
    const currentEndValue = endTime || originalEndValue;
    const originalMaxCapacityValue = props.schedule?.max_capacity;
    const hasOriginalCapacity = originalMaxCapacityValue !== undefined && originalMaxCapacityValue !== null;
    const normalizedOriginalMaxCapacity = hasOriginalCapacity ? Number(originalMaxCapacityValue) : null;
    const currentMaxCapacity = maxCapacity === '' || maxCapacity === null
      ? (hasOriginalCapacity ? Number(originalMaxCapacityValue) : null)
      : Number(maxCapacity);
    const originalStartMoment = originalStartValue ? moment(originalStartValue) : null;
    const originalEndMoment = originalEndValue ? moment(originalEndValue) : null;
    const currentStartMoment = currentStartValue ? moment(currentStartValue) : null;
    const currentEndMoment = currentEndValue ? moment(currentEndValue) : null;
    const startChanged = originalStartMoment ? !currentStartMoment?.isSame(originalStartMoment) : !!currentStartMoment;
    const endChanged = originalEndMoment ? !currentEndMoment?.isSame(originalEndMoment) : !!currentEndMoment;

    const scheduleChanged = (
      currentDateValue !== originalDate ||
      startChanged ||
      endChanged ||
      sessionNotes !== (props.schedule?.notes || '') ||
      scheduleName !== (props.schedule?.schedule_name || '') ||
      String(taskType || '') !== String(props.schedule?.task_type || '') ||
      currentMaxCapacity !== normalizedOriginalMaxCapacity
    );
    const customerChanged = selectedCustomers.length > 0 || customersToRemove.length > 0;
    const employeeChanged = selectedEmployees.length > 0 || employeesToRemove.length > 0;
    if(props.user.permissions.ScheduleSessionForOtherEmployees) {
      errors = {
        selectedCustomers: !customerChanged && !employeeChanged && !scheduleChanged,
        selectedEmployees: !employeeChanged && !customerChanged && !scheduleChanged,
        startTime: !startTime || moment(startTime).isSameOrAfter(moment(endTime)),
        endTime: !endTime || moment(endTime).isSameOrBefore(moment(startTime))
      };
    } else {
      errors = {
        selectedCustomers: !customerChanged && !scheduleChanged,
        startTime: !startTime || moment(startTime).isSameOrAfter(moment(endTime)),
        endTime: !endTime || moment(endTime).isSameOrBefore(moment(startTime))
      };
    }
    setErrors(errors);
    setSubmitted(true);
    return !Object.values(errors).some(err => err);
  };

  const unassignedCustomers = () => {
    let scheduledSessions = props.sessions.filter(session => session.schedule_id === props.schedule?.schedule_id);
    let scheduleCustomers = scheduledSessions.map(session => session.customer_id);
    const removalIds = new Set(customersToRemove.map(item => item.customer.cus_id));
    return props.customers.filter(customer => !scheduleCustomers.includes(customer.cus_id) || removalIds.has(customer.cus_id));
  }

  const assginedCustomers = () => {
    let scheduledSessions = props.sessions.filter(session => session.schedule_id === props.schedule?.schedule_id);
    let scheduleCustomers = scheduledSessions.map(session => session.customer_id);
    const removalIds = new Set(customersToRemove.map(item => item.customer.cus_id));
    return props.customers.filter(customer => scheduleCustomers.includes(customer.cus_id) && !removalIds.has(customer.cus_id));
  }

  const unassginedEmployees = () => {
    const removalIds = new Set(employeesToRemove.map(emp => emp.id));
    if (!props.schedule?.employee_ids) {
      return props.employees;
    }
    return props.employees.filter(employee => !props.schedule.employee_ids.includes(employee.id) || removalIds.has(employee.id));
  }

  const assginedEmployees = () => {
    if (!props.schedule?.employee_ids) {
      return [];
    }
    const removalIds = new Set(employeesToRemove.map(emp => emp.id));
    return props.employees.filter(employee => props.schedule.employee_ids.includes(employee.id) && !removalIds.has(employee.id));
  }

  useEffect(() => {
    if (!props.open) {
      setSubmitted(false);
      setErrors({
        selectedCustomers: false,
        selectedEmployees: false,
        startTime: false,
        endTime: false
      });
      setSelectedCustomers([]);
      setSelectedEmployees([]);
      setNotes('');
      setCustomersToRemove([]);
      setEmployeesToRemove([]);
      setApplyChangesToFuture(false);
      isSubmittingRef.current = false;
      initialAssignedCustomerIdsRef.current = [];
      initialAssignedEmployeeIdsRef.current = [];
      return;
    }

    const noScheduleData =
      scheduleId === undefined &&
      scheduleDateRaw === undefined &&
      scheduleStartTime === undefined &&
      scheduleEndTime === undefined &&
      scheduleNotes === undefined &&
      scheduleTaskType === undefined &&
      scheduleMaxCapacity === undefined;

    if (noScheduleData) {
      return;
    }

    const scheduleDate = scheduleDateRaw ? scheduleDateRaw.substring(0, 10) : '';

    setSubmitted(false);
    setErrors({
      selectedCustomers: false,
      selectedEmployees: false,
      startTime: false,
      endTime: false
    });
    setSelectedCustomers([]);
    setSelectedEmployees([]);
    setNotes('');
    setCustomersToRemove([]);
    setEmployeesToRemove([]);
    setApplyChangesToFuture(false);
    setStartDate(scheduleDate);
    setStartTime(scheduleStartTime);
    setEndTime(scheduleEndTime);
    setSessionNotes(scheduleNotes || '');
    setScheduleName(props.schedule?.schedule_name || '');
    setTaskType(scheduleTaskType || '');
    setMaxCapacity(scheduleMaxCapacity ?? '');

    initialDataRef.current = {
      startDate: scheduleDate,
      startTime: scheduleStartTime ? moment(scheduleStartTime).format('HH:mm') : '',
      endTime: scheduleEndTime ? moment(scheduleEndTime).format('HH:mm') : '',
      sessionNotes: scheduleNotes || '',
      scheduleName: props.schedule?.schedule_name || '',
      taskType: scheduleTaskType || '',
      maxCapacity: String(scheduleMaxCapacity ?? ''),
    };

    // Capture initial assigned customer IDs for diff calculation on submit
    const scheduledSessions = props.sessions.filter(session => session.schedule_id === scheduleId);
    initialAssignedCustomerIdsRef.current = scheduledSessions.map(session => session.customer_id);
    
    // Capture initial assigned employee IDs for diff calculation on submit
    initialAssignedEmployeeIdsRef.current = Array.isArray(props.schedule?.employee_ids) ? [...props.schedule.employee_ids] : [];
  }, [
    props.open,
    scheduleId,
    scheduleDateRaw,
    scheduleStartTime,
    scheduleEndTime,
    scheduleNotes,
    scheduleTaskType,
    scheduleMaxCapacity
  ]);

  useEffect(() => {
    if(props.isDayCare && taskType) {
      axios
      .get(APIURL + "/preAuth/taskType", {
              params: { 
                companyID: props.comp_id, 
                fromDate: moment(startDate).isValid() ? moment(startDate).subtract(2, 'months').format('MM/DD/YYYY') : null,
                toDate: moment(startDate).isValid() ? moment(startDate).endOf("day").format('MM/DD/YYYY') : null,
                taskType
              },
            })
        .then(response => setPreAuthList(response.data.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))))
        .catch(error => console.error(error));
    } else {
      setPreAuthList([]);
    }
  }, [taskType, props.isDayCare, props.comp_id, startDate]);

  const isDirty = () => {
    const snap = initialDataRef.current;
    if (!snap) return false;
    const current = {
      startDate: startDate || snap.startDate,
      startTime: startTime ? moment(startTime).format('HH:mm') : snap.startTime,
      endTime: endTime ? moment(endTime).format('HH:mm') : snap.endTime,
      sessionNotes: sessionNotes || '',
      scheduleName: scheduleName || '',
      taskType: taskType || '',
      maxCapacity: String(maxCapacity ?? ''),
    };
    if (selectedCustomers.length > 0) return true;
    if (selectedEmployees.length > 0) return true;
    if (customersToRemove.length > 0) return true;
    if (employeesToRemove.length > 0) return true;
    if (applyChangesToFuture) return true;
    return (
      current.startDate !== snap.startDate ||
      current.startTime !== snap.startTime ||
      current.endTime !== snap.endTime ||
      current.sessionNotes !== snap.sessionNotes ||
      current.scheduleName !== snap.scheduleName ||
      current.taskType !== snap.taskType ||
      current.maxCapacity !== snap.maxCapacity
    );
  };

  const handleAttemptClose = (event, reason) => {
    if (isDirty()) {
      setShowConfirmClose(true);
    } else {
      props.onClose();
    }
  };

  const handleDiscardChanges = () => {
    setShowConfirmClose(false);
    props.onClose();
  };

  return (
    <>
    <Modal enforceFocus={false}
      open={props.open}
      // onClose={handleAttemptClose}
      aria-labelledby="assign-entity-modal-title"
      aria-describedby="assign-entity-modal-description"
    >
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 500, maxWidth: '90%', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, maxHeight: '95vh', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography id="assign-entity-modal-title" variant="h6" component="div" sx={{ mb: 1 }}>
            Edit Session
          </Typography>
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.25 }}
            onClick={handleAttemptClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <TextField
          label="Session Name"
          fullWidth
          value={scheduleName}
          onChange={(e) => setScheduleName(e.target.value)}
          inputProps={{ maxLength: 50 }}
          sx={{ mb: 1, mt: 0.5 }}
        />
        <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
          {props.schedule?.task_type && <span>{props.schedule?.task_type} </span>}
          {props.resources.find(resource => resource.resource_id === props.schedule?.resource_id)?.resource_name} {moment(props.schedule?.date).utc().format('MM/DD/YYYY')} {moment(props.schedule?.start_time).format('hh:mm A')} - {moment(props.schedule?.end_time).format('hh:mm A')}
        </Typography>
        {props.user.permissions.ScheduleSessionForOtherEmployees && (
          <>
            <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
              Assigned Employees:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {assginedEmployees().map(employee => (
                <Chip key={employee.id} label={employee.employee_name} color="primary" sx={{ mb: .15}} onDelete={() => handleDeleteEmployee(employee)} />
              ))}
            </Box>
            <FormControl fullWidth margin="normal" sx={{ mt: 1, mb: 1 }}>
              <Autocomplete
                multiple
                id="employee-select"
                options={unassginedEmployees()}
                getOptionLabel={(option) => option.employee_name}
                defaultValue={[]}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Employees"
                    placeholder="Employees"
                    error={errors.selectedEmployees && submitted}
                  />
                )}
                onChange={(event, newValue) => {
                  const filtered = newValue.filter(item => item && item.id);
                  setEmployeesToRemove(prev => prev.filter(emp => !filtered.some(sel => sel.id === emp.id)));
                  setSelectedEmployees(filtered.filter(item => !props.schedule?.employee_ids?.includes(item.id)));
                }}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.id}>
                      <ListItemText primary={option.employee_name} />
                    </li>
                  )
                }}
              />
            </FormControl>
          </>
        )}

        <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
          Assigned {entityLabelPlural}:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {assginedCustomers().map(customer => (
            <Chip key={customer.cus_id} label={customer.cus_name} color="primary" sx={{ mb: .15}} onDelete={() => handleDeleteCustomer(customer)} />
          ))}
          {customersToRemove.map(({ customer }) => (
            <Chip
              key={`remove-${customer.cus_id}`}
              label={customer.cus_name}
              color="error"
              variant="outlined"
              onDelete={() => setCustomersToRemove(prev => prev.filter(item => item.customer.cus_id !== customer.cus_id))}
            />
          ))}
        </Box>
        {props.user.permissions.ScheduleSessionForOtherEmployees && employeesToRemove.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {employeesToRemove.map(employee => (
              <Chip
                key={`remove-emp-${employee.id}`}
                label={employee.employee_name}
                color="error"
                variant="outlined"
                onDelete={() => setEmployeesToRemove(prev => prev.filter(item => item.id !== employee.id))}
              />
            ))}
          </Box>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <FormControl fullWidth margin="normal" sx={{ mt: 1, mb: 1 }}>
            <Autocomplete
              multiple
              id="customer-select"
              options={[assignAllOption, ...unassignedCustomers()]}
              value={selectedCustomers} // controlled value
              getOptionLabel={(option) => option.cus_name}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label={`Select ${entityLabelPlural}`}
                    placeholder={entityLabelPlural}
                    error={errors.selectedCustomers && submitted}
                />
              )}
              open={customersOpen}
              onOpen={() => {
                setCustomersOpen(true);
              }}
              onClose={(event, reason) => {
                if (reason === 'selectOption' && event.target.innerText !== 'Select All') {
                  setCustomersOpen(true);
                } else {
                  setCustomersOpen(false);
                }
              }}
              onChange={(event, newValue) => {
                const removalIds = new Set(customersToRemove.map(item => item.customer.cus_id));
                let next = newValue.filter(item => item && item.cus_id && item.cus_id !== 'assign_all');

                if (newValue.some(item => item?.cus_id === 'assign_all')) {
                  next = unassignedCustomers();
                }

                const idsToRestore = next
                  .filter(item => removalIds.has(item.cus_id))
                  .map(item => item.cus_id);

                if (idsToRestore.length > 0) {
                  setCustomersToRemove(prev => prev.filter(item => !idsToRestore.includes(item.customer.cus_id)));
                }

                const additions = next
                  .filter(item => !removalIds.has(item.cus_id))
                  .filter(item => !props.sessions.some(session => session.schedule_id === props.schedule?.schedule_id && session.customer_id === item.cus_id));

                setSelectedCustomers(additions);
              }}
              renderOption={(props, option) => {
                const preAuth = preAuthList.find(item => item.cus_id === option.cus_id);
                const remainingUnits = preAuth ? Math.max(0, preAuth.remainingUnits) : null;
                const unitString = remainingUnits !== null ?
                  `${remainingUnits}${remainingUnits === 1 ? ' Unit' : ' Units'}` : '';
                 return (
                  <li {...props} key={option.cus_id}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      {option.cus_id === 'assign_all' ? (
                        <span style={{ color: '#3fa7d6', cursor: 'pointer' }}>Select All</span>
                      ) : (
                        <>
                          <span>{option.cus_name}</span>
                          {option.cus_status === 'Prospect' && (
                            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'gray' }}>Prospect</span>
                          )}
                          {unitString && <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'gray' }}>{unitString}</span>}
                        </>
                      )}
                    </div>
                  </li>
                )
              }}
            />
          </FormControl>
          <Grid container spacing={2} alignItems="center" sx={{ mt: 0, mb: 2 }}>
            <Grid size={8}>
              <FormControl fullWidth>
                <InputLabel id="task-type-label">Task Type</InputLabel>
                <Select
                  labelId="task-type-label"
                  id="task-type-select"
                  value={taskType}
                  label="Task Type"
                  onChange={(e) => setTaskType(e.target.value)}
                >
                  {props.taskTypes.map((taskType) => (
                    <MenuItem key={taskType} value={taskType}>
                      {taskType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={4}>
              <TextField
                label="Max Capacity"
                type="number"
                value={maxCapacity}
                onChange={(e) => setMaxCapacity(e.target.value)}
                fullWidth
                inputProps={{
                  min: 1,
                  max: (props.resources?.find(r => r.resource_id === props.schedule?.resource_id)?.capacity) || ''
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex ', flexDirection: 'row', gap: 1, mb: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={startDate ? moment(startDate).toDate() : null}
                sx={{ width: '36%' }}
                onChange={(newValue) => { 
                  if (newValue && moment(newValue).isValid()) {
                    setStartDate(moment(newValue).format('YYYY-MM-DD'));
                  } else {
                    setStartDate('');
                  }
                }}
              />
              <TimePicker
                label="Start Time"
                value={new Date(startTime)}
                slotProps={{ textField: { error: errors.startTime && submitted } }}
                sx={{ width: '32%' }}
                onChange={(newValue) => setStartTime(newValue)}
              />
              <TimePicker
                label="End Time"
                value={new Date(endTime)}
                slotProps={{ textField: { error: errors.endTime && submitted } }}
                sx={{ width: '32%' }}
                onChange={(newValue) => setEndTime(newValue)}
              />
            </LocalizationProvider>
          </Box>
          <TextField
            margin="normal"
            fullWidth
            label="Session Notes"
            multiline
            rows={4}
            value={sessionNotes}
            onChange={(e) => setSessionNotes(e.target.value)}
            sx={{ mb: 1 }}
            inputProps={{ maxLength: 2000 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={applyChangesToFuture}
                onChange={(e) => setApplyChangesToFuture(e.target.checked)}
              />
            }
            label="Apply changes to future sessions"
          />
{/*           <TextField
            margin="normal"
            fullWidth
            label="Customer Notes"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mb: 2 }}
          /> */}
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 1 }}>
            Edit Session
          </Button>
        </Box>
      </Box>
  </Modal>
  <Dialog open={showConfirmClose} onClose={() => setShowConfirmClose(false)}>
      <DialogTitle>Discard changes?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You have unsaved changes. Are you sure you want to close without saving?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowConfirmClose(false)}>Continue Editing</Button>
        <Button color="error" onClick={handleDiscardChanges} autoFocus>Discard</Button>
      </DialogActions>
    </Dialog>
  </>
  );
}

export default AssignCustomerAndEmployeeModal;
